/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getFocusStyle, getTheme, mergeStyles } from '@fluentui/style-utilities';
import { Property } from 'csstype';
import * as React from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';
import { getScopedTheme, getStylist, NestedCSSProperties } from 'src/core/themes/luisStylist';

const { styleComponent } = getStylist('HeaderNavigationLink');
const linkTheme = getScopedTheme('HeaderNavigationLink');

/**
 * Renders the blue selection indicator below the
 * navigation header link.
 *
 * @param opacity The opacity to render it with.
 */
const selectionIndicator = (opacity: 0 | 1): Record<string, NestedCSSProperties> => ({
    '&::before': {
        opacity,
        left: 0,
        right: 0,
        bottom: 0,
        height: 5,
        content: '\' \'',
        position: 'absolute',
        transition: 'opacity .2s ease',
        backgroundColor: linkTheme.selectionIndicatorColor
    }
});

const HeaderNavigationLinkRoot = styleComponent(NavLink)('NavigationItem', theme => ({
    height: '100%',
    display: 'flex',
    cursor: 'pointer',
    position: 'relative',
    alignItems: 'center',
    textDecoration: 'none',
    color: theme.fontColor,
    padding: theme.padding,
    textTransform: 'uppercase',
    transition: 'color background-color .2s ease',
    fontWeight: theme.fontWeight as Property.FontWeight,
    $nest: {
        ...selectionIndicator(0),
        '&:hover': { backgroundColor: theme.hoverBackgroundColor },
        '&.active': { $nest: selectionIndicator(1), backgroundColor: theme.hoverBackgroundColor }
    }
}));

const focusStyles = mergeStyles(getFocusStyle(getTheme(), 2));

/**
 * Renders a header navigation link. Mainly used in
 * the application headers.
 */
export const HeaderNavigationLink = (props: React.PropsWithChildren<NavLinkProps>) => {
    const { children, ...rest } = props;

    return (
        <HeaderNavigationLinkRoot {...rest} className={focusStyles}>
            {props.children}
        </HeaderNavigationLinkRoot>
    );
};
